import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi } from "../../api/adminApi";
import { useAuth } from "../../auth/AuthContext";
import { useDeployment } from "../../deployment/DeploymentContext";
import { InstallationId, LicenseUpload } from "./LicenseUpload";

/** Shown instead of the app when an on-premise license has expired (or is missing). Sign-in works
 *  so an administrator can get here and upload the renewal; nothing else in the app does until then. */
export default function LicenseGate() {
  const { status, refresh } = useDeployment();
  const { hasRole, logout, user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canRenew = hasRole("OWNER", "ADMIN");
  const missing = status?.status === "UNLICENSED";

  async function handleInstall(key: string) {
    setBusy(true);
    setError(null);
    try {
      await adminApi.license.install(key);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not install the license");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="setup-shell">
      <div className="setup-card">
        <div className="setup-brand">
          <div className="app-brand-mark">RF</div>
          <div>
            <div className="setup-title">{missing ? "License required" : "License expired"}</div>
            <div className="setup-sub">{status?.organisationName ?? "ReportForge"}</div>
          </div>
        </div>

        <div className="license-alert">
          {missing ? (
            <>This installation has no active license.</>
          ) : (
            <>
              The license{status?.licensee ? <> for <strong>{status.licensee}</strong></> : null} expired
              {status?.expiresAt ? <> on <strong>{new Date(status.expiresAt).toLocaleDateString()}</strong></> : null}.
            </>
          )}{" "}
          Reports, schedules and integrations are paused until a renewed license is uploaded. Your data is safe and
          untouched.
        </div>

        {canRenew ? (
          <>
            <h3 style={{ marginTop: 20 }}>Upload the renewed license</h3>
            <p className="help-text">
              Request a renewal from the vendor (quote the installation ID below), then upload the new license file.
              Everything resumes immediately — no restart needed.
            </p>
            <div className="form-row" style={{ maxWidth: "none" }}>
              <label>Installation ID</label>
              <InstallationId id={status?.installationId ?? null} />
            </div>
            {error && <div className="error-text">{error}</div>}
            <LicenseUpload submitLabel="Activate renewed license" busy={busy} onSubmit={handleInstall} />
          </>
        ) : (
          <p className="help-text" style={{ marginTop: 16 }}>
            Ask an administrator of your organisation to upload a renewed license. Signed in as {user?.email}.
          </p>
        )}

        <button
          type="button"
          className="btn-secondary"
          style={{ marginTop: 20 }}
          onClick={async () => {
            await logout();
            navigate("/login");
          }}
        >
          Log out
        </button>
      </div>
    </div>
  );
}
